import { useCallback, SyntheticEvent } from 'react';
import { useTranslation, TFunction } from 'react-i18next';

import { Divider, FilterItem, Icon, Input, Select, TOption } from '@treezor/design-system';

import { RenderSelectedValues } from './RenderSelectedValues';
import { IRange, IRangeField, rangeBases } from './filters.type';

export interface IRangeFilterProps {
  field: IRangeField;
  label: string;
  selectBase?: TOption[];
}

export function parseBasesRange(t: TFunction<'translation', undefined>, bases = rangeBases) {
  return Object.keys(bases).map((base) => ({
    label: t(`components.molecules.rangeBases.${base}`),
    value: base,
  }));
}

export function isFrom(value: IRange) {
  return (value?.base === rangeBases.EQUAL || value.base === rangeBases.MORE) && !!value.from;
}

export function isTo(value: IRange) {
  return value.base === rangeBases.LESS && !!value.to;
}

export function isBetween(value: IRange) {
  return value.base === rangeBases.BETWEEN && !!value.from && !!value.to;
}

export function RangeFilter({ label, field, selectBase }: IRangeFilterProps) {
  const { t } = useTranslation();

  const handleBaseChange = useCallback(
    (value) => {
      field?.handleChange({ base: value, from: null, to: null });
    },
    [field],
  );

  const handleFromChange = useCallback(
    (value) => {
      field?.handleChange({ from: value });
    },
    [field],
  );

  const handleToChange = useCallback(
    (value) => {
      field?.handleChange({ to: value });
    },
    [field],
  );

  const handleResetChange = useCallback(() => {
    field?.handleChange({ base: null, from: null, to: null });
  }, [field]);

  const getValuesLabel = useCallback(() => {
    if (!!field.value && isFrom(field.value)) {
      return [`${t(`components.molecules.rangeBases.${field.value.base}`)} ${field.value.from}€`];
    } else if (!!field.value && isTo(field.value)) {
      return [`${t(`components.molecules.rangeBases.${field.value.base}`)} ${field.value.to}€`];
    } else if (!!field.value && isBetween(field.value)) {
      return [`${t(`components.molecules.rangeBases.${field.value.base}`)} ${field.value.from}€ - ${field.value.to}€`];
    }
    return undefined;
  }, [field.value, t]);

  const iconRight = (
    <div className="flex gap-3 items-center h-full">
      <Divider direction="vertical" />
      <div className="text-xs w-fit text-nowrap text-primary">EUR (€)</div>
    </div>
  );

  return (
    <FilterItem
      type="CUSTOM"
      id="status"
      renderSelectedValues={(closeDropDown) => (
        <RenderSelectedValues
          label={label}
          values={getValuesLabel()}
          onReset={() => {
            handleResetChange();
            closeDropDown();
          }}
        />
      )}
      dropdownContent={(closeDropDown) => (
        <div className="flex flex-col py-3 px-4">
          <div className="flex items-center justify-start gap-3">
            {!!selectBase && (
              <Select
                options={selectBase}
                defaultValue={field.value?.base || selectBase[0].value}
                size="s"
                onChange={(value) => handleBaseChange(value)}
              />
            )}
            {field.value?.base !== rangeBases.LESS && (
              <Input
                size="s"
                type="number"
                placeholder=""
                icon={{
                  right: iconRight,
                }}
                defaultValue={field.value?.from}
                onBlur={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                  const value = event.currentTarget.value;
                  handleFromChange(value);
                  if (field.value?.base === rangeBases.MORE || field.value?.base === rangeBases.EQUAL) {
                    closeDropDown();
                  }
                }}
                className="max-w-10"
              />
            )}
            {(field.value?.base === rangeBases.LESS || field.value?.base === rangeBases.BETWEEN) && (
              <>
                <span>
                  <Icon name="arrow_right_alt" size="s" />
                </span>
                <Input
                  size="s"
                  type="number"
                  placeholder=""
                  icon={{
                    right: iconRight,
                  }}
                  defaultValue={field.value?.to}
                  onBlur={(event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                    handleToChange(event.currentTarget.value);
                    if (field.value?.base === rangeBases.LESS) {
                      closeDropDown();
                    }
                  }}
                  className="max-w-10"
                />
              </>
            )}
          </div>
        </div>
      )}
    />
  );
}
